import { Download, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { apiGet, apiPost, apiSend, ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

/**
 * ZRZUT EKRANU MASZYNY — okienko, w którym obraz się POKAZUJE i UMIERA.
 *
 * ⚠️ PO CO TO JEST: portal widzi, że proces SPSUI chodzi, ale nie widzi okna „nie mogę połączyć się
 * z bazą", monitu „nowa wersja Windows — przeklikaj trzy okna" ani przeglądarki, którą ktoś odpalił
 * przy automacie. Jedno spojrzenie zamiast dojazdu do klienta.
 *
 * ⚠️ OBRAZ ZNIKA PRZY ZAMKNIĘCIU. `DELETE` leci w `finally` zamknięcia, a gdyby ktoś zamknął kartę
 * przeglądarki, obraz i tak wygaśnie po stronie API (kwadrans). Ten komponent nie trzyma go nigdzie
 * poza pamięcią widoku — nie ma cache'u react-query, bo pulpit maszyny klienta nie jest danymi
 * do buforowania.
 *
 * ⚠️ ODPYTUJEMY, BO KANAŁ JEST POLLINGOWY. Agent nie ma otwartego portu — sam puka do portalu
 * w cyklu, więc od kliknięcia do obrazu mija do kilkudziesięciu sekund. Okienko musi więc mówić,
 * na co czeka (`note` od agenta), a nie kręcić kółkiem bez słowa.
 */

type Stan = {
  status: string;
  note: string | null;
  error: string | null;
  obraz: string | null;
  szerokosc: number | null;
  wysokosc: number | null;
  wariant: string | null;
  bajtow: number | null;
};

/**
 * Jak często okienko pyta portal o gotowy obraz.
 *
 * ⚠️ 400 MS, NIE SEKUNDA — ostatni z trzech progów, które składały się na „czekamy tyle czasu na
 * plik 100 kB". Agent pyta o zlecenie co 2 s, pomocnik oddaje obraz w ułamku sekundy, więc
 * sekundowe odpytywanie dokładało średnio pół sekundy do rzeczy, która ma zająć dwie. Koszt: jedno
 * lekkie zapytanie na 400 ms i TYLKO przy otwartym okienku — po zamknięciu pętla gaśnie z komponentem.
 */
const ODSTEP_MS = 400;
/** Po tym czasie przestajemy pytać — zlecenie i tak przepada po pięciu minutach po stronie API. */
const CIERPLIWOSC_MS = 5 * 60 * 1000;

export function ZrzutEkranu({
  maszyna,
  groupId,
  onClose,
}: {
  maszyna: { id: string; name: string } | null;
  groupId: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [stan, setStan] = useState<Stan | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  /** ⚠️ W refie, nie w stanie: sprzątanie przy zamknięciu musi widzieć AKTUALNY identyfikator. */
  const zadanieRef = useRef<string | null>(null);
  const maszynaRef = useRef<string | null>(null);

  const otwarte = maszyna !== null;

  useEffect(() => {
    if (maszyna === null) return;
    let zyje = true;
    const start = Date.now();
    let uchwyt: ReturnType<typeof setTimeout> | undefined;

    setStan(null);
    setBlad(null);
    maszynaRef.current = maszyna.id;

    const pytaj = async (id: string): Promise<void> => {
      try {
        const s = await apiGet<Stan>(`/machines/${maszyna.id}/zrzut/${id}?groupId=${groupId}`);
        if (!zyje) return;
        setStan(s);
        const koniec = s.status === 'GOTOWY' || s.status === 'FAILED' || s.status === 'WYGASL';
        if (koniec) return;
        if (Date.now() - start > CIERPLIWOSC_MS) {
          setBlad(t('screenshot.timeout'));
          return;
        }
        uchwyt = setTimeout(() => void pytaj(id), ODSTEP_MS);
      } catch (e) {
        if (!zyje) return;
        setBlad(e instanceof ApiError ? e.message : String(e));
      }
    };

    void (async () => {
      try {
        const { taskId: id } = await apiPost<{ taskId: string; dolaczone: boolean }>(
          `/machines/${maszyna.id}/zrzut?groupId=${groupId}`,
        );
        if (!zyje) return;
        zadanieRef.current = id;
        void pytaj(id);
      } catch (e) {
        if (!zyje) return;
        /** Odmowy z API są tu treścią, nie awarią: „agent milczy od 40 min" to pełna odpowiedź. */
        setBlad(e instanceof ApiError ? e.message : String(e));
      }
    })();

    return () => {
      zyje = false;
      if (uchwyt !== undefined) clearTimeout(uchwyt);
    };
  }, [maszyna, groupId, t]);

  /**
   * ⚠️ ZAMKNIĘCIE USUWA OBRAZ Z API. Bez tego wisiałby do wygaśnięcia — a nie ma powodu, żeby
   * pulpit maszyny klienta leżał w bazie choćby minutę dłużej, niż ktoś na niego patrzy.
   */
  const zamknij = useCallback(() => {
    const id = zadanieRef.current;
    const m = maszynaRef.current;
    zadanieRef.current = null;
    if (id !== null && m !== null) {
      void apiSend('DELETE', `/machines/${m}/zrzut/${id}?groupId=${groupId}`).catch(() => {});
    }
    onClose();
  }, [groupId, onClose]);

  const zapisz = useCallback(() => {
    if (stan?.obraz === undefined || stan?.obraz === null || maszyna === null) return;
    const a = document.createElement('a');
    a.href = stan.obraz;
    /**
     * Nazwa pliku z automatem i czasem — operator zapisuje to do zgłoszenia, więc „zrzut(3).png"
     * byłoby dokładnie tym, czego nie chce.
     */
    const czas = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const rozszerzenie = stan.obraz.startsWith('data:image/jpeg') ? 'jpg' : 'png';
    a.download = `zrzut-${maszyna.name.replace(/[^\w.-]+/g, '_')}-${czas}.${rozszerzenie}`;
    a.click();
  }, [stan, maszyna]);

  const gotowy = stan?.status === 'GOTOWY' && stan.obraz !== null;
  const opis = (() => {
    if (blad !== null) return blad;
    if (stan === null) return t('screenshot.ordering');
    if (stan.status === 'FAILED') return stan.error ?? t('screenshot.failed');
    if (stan.status === 'WYGASL') return t('screenshot.expired');
    if (gotowy) {
      const kb = stan.bajtow === null ? '' : ` · ${Math.round(stan.bajtow / 102.4) / 10} KB`;
      return `${stan.szerokosc}×${stan.wysokosc}${kb}`;
    }
    return stan.note ?? t('screenshot.waiting');
  })();

  return (
    <Dialog open={otwarte} onOpenChange={(o) => !o && zamknij()}>
      {/**
        * ⚠️ NADPISUJEMY SZEROKOŚĆ, NIE `max-w` — i to jest naprawa błędu, nie zmiana gustu.
        * Bazowy `DialogContent` ustawia KONKRETNĄ szerokość `w-[min(32rem,…)]`, czyli 512 px.
        * Podane tu wcześniej `max-w-4xl` nie robiło NIC, bo szerokość była już ustalona — okno
        * miało 512 px i zrzut ekranu automatu (zwykle 1920 px szeroki) schodził do ~27 % skali.
        * Zgłoszone (26.08.2026): „okienko jest bardzo małe względem screenshota".
        *
        * ⚠️ `max-w-none` JEST KONIECZNE obok szerokości: bez niego zostaje bazowy limit i całość
        * wraca do rozmiaru, z którego właśnie wychodzimy.
        */}
      <DialogContent
        title={`${t('screenshot.title')} — ${maszyna?.name ?? ''}`}
        description={opis}
        className="w-[min(110rem,calc(100vw-2rem))] max-w-none"
      >
        <div className="flex min-h-64 items-center justify-center rounded-md border bg-muted/30 p-2">
          {gotowy ? (
            /**
             * ⚠️ `data:`-URL, bo `img-src 'self' data:` jest już w naszym CSP, a ten sam napis
             * obsługuje i podgląd, i zapis na dysk. Obraz skalujemy DO OKNA, nigdy w górę —
             * powiększony pulpit rozmywa dokładnie ten tekst błędu, po który się tu przyszło.
             */
            <img
              src={stan.obraz ?? ''}
              alt={`${t('screenshot.title')} — ${maszyna?.name ?? ''}`}
              /**
               * ⚠️ `object-contain` + `w-auto` ZOSTAJĄ: obraz nie ma być rozciągany ani powiększany
               * powyżej rozmiaru naturalnego — zrzut ekranu automatu czyta się tylko wtedy, gdy
               * piksele są jego własne. Podnosimy sam limit wysokości, żeby szeroki zrzut mógł
               * skorzystać z nowej szerokości okna.
               */
              className="max-h-[78vh] w-auto max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
              {blad === null && stan?.status !== 'FAILED' && stan?.status !== 'WYGASL' ? (
                <Loader2 className="size-8 animate-spin" />
              ) : null}
              <span className="max-w-lg text-center text-sm">{opis}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={zamknij}>
            {t('common.close')}
          </Button>
          <Button type="button" onClick={zapisz} disabled={!gotowy}>
            <Download />
            {t('screenshot.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
